import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormField } from '@/components/shared/form-field';
import { MinisterCombobox } from '@/components/shared/minister-combobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMemberPicker } from '@/pages/pastoral/use-member-picker';

import {
  emptyMinister,
  MAX_OFFICIATING_MINISTERS,
  type MinisterDraft,
  type WitnessDraft,
} from './marriage-party-state';

const WitnessSlot = ({
  enabled,
  scope,
  label,
  nameLabel,
  witness,
  onChange,
}: {
  enabled: boolean;
  scope: string;
  label: string;
  nameLabel: string;
  witness: WitnessDraft;
  onChange: (next: WitnessDraft) => void;
}) => {
  const { t } = useTranslation('sacraments');
  const { t: tCommon } = useTranslation();
  const picker = useMemberPicker(enabled, scope);

  return (
    <>
      <FormField label={label}>
        {(field) => (
          <EntityCombobox
            id={field.id}
            value={witness.memberId}
            onChange={(id) => {
              if (!id) {
                onChange({ memberId: null, name: witness.name });
                return;
              }
              const selectedLabel =
                picker.options.find((row) => row.id === id)?.label ??
                (witness.memberId === id ? witness.name : '');
              onChange({ memberId: id, name: selectedLabel });
            }}
            options={picker.options}
            selected={
              witness.memberId
                ? { id: witness.memberId, label: witness.name || witness.memberId }
                : null
            }
            onQueryChange={picker.setQuery}
            loading={picker.loading}
            placeholder={t('memberPicker.placeholder')}
            emptyMessage={t('memberPicker.empty')}
            clearLabel={tCommon('action.clearSelection')}
            popoverPortal="dialog"
          />
        )}
      </FormField>
      {!witness.memberId ? (
        <FormField label={nameLabel}>
          {(field) => (
            <Input
              id={field.id}
              value={witness.name}
              onChange={(event) => onChange({ memberId: null, name: event.target.value })}
            />
          )}
        </FormField>
      ) : null}
    </>
  );
};

export const MarriageWitnessPair = ({
  enabled,
  scope,
  witnesses,
  onChange,
  labelFor,
  nameLabelFor,
}: {
  enabled: boolean;
  scope: string;
  witnesses: [WitnessDraft, WitnessDraft];
  onChange: (next: [WitnessDraft, WitnessDraft]) => void;
  labelFor: (number: number) => string;
  nameLabelFor: (number: number) => string;
}) => (
  <>
    {([0, 1] as const).map((index) => (
      <WitnessSlot
        key={index}
        enabled={enabled}
        scope={`${scope}-${index + 1}`}
        label={labelFor(index + 1)}
        nameLabel={nameLabelFor(index + 1)}
        witness={witnesses[index]}
        onChange={(next) => {
          const pair: [WitnessDraft, WitnessDraft] = [witnesses[0], witnesses[1]];
          pair[index] = next;
          onChange(pair);
        }}
      />
    ))}
  </>
);

export const MarriageMinistersFields = ({
  enabled,
  ministers,
  onChange,
}: {
  enabled: boolean;
  ministers: MinisterDraft[];
  onChange: (next: MinisterDraft[]) => void;
}) => {
  const { t } = useTranslation('sacraments');
  const { t: tCommon } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <p className="text-caption text-fg-secondary">{t('marriages.fields.officiatingMinistersHint')}</p>
      {ministers.map((minister, index) => (
        <FormField
          key={minister.key}
          label={
            ministers.length === 1
              ? t('marriages.fields.officiatingMinister')
              : t('marriages.fields.officiatingMinisterNumber', { number: index + 1 })
          }
        >
          {(field) => (
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <MinisterCombobox
                  id={field.id}
                  value={minister.name}
                  onChange={(name) =>
                    onChange(
                      ministers.map((row) => (row.key === minister.key ? { ...row, name } : row)),
                    )
                  }
                  enabled={enabled}
                  placeholder={t('ministerPicker.placeholder')}
                  emptyMessage={t('ministerPicker.empty')}
                  guestLabel={(name) => t('ministerPicker.guest', { name })}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              </div>
              {ministers.length > 1 ? (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={t('marriages.fields.removeMinister', { number: index + 1 })}
                  onClick={() => {
                    const next = ministers.filter((row) => row.key !== minister.key);
                    onChange(next.length > 0 ? next : [emptyMinister()]);
                  }}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              ) : null}
            </div>
          )}
        </FormField>
      ))}
      {ministers.length < MAX_OFFICIATING_MINISTERS ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            leadingIcon={<Plus className="size-4" aria-hidden />}
            onClick={() => onChange([...ministers, emptyMinister()])}
          >
            {t('marriages.fields.addMinister')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
